import { Box, Link } from '@amzn/awsui-components-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import { StatusType } from 'src/components/fintech-ops/sas/SasInterfaces';
import { returnFinSuiteUniqueUsersData } from 'src/components/fintech-ops/user-metrics/UserMetricsDataProcessing';
import { UserMetricsHomePageFinSuite } from 'src/components/fintech-ops/user-metrics/UserMetricsInterfaces';
import { logger } from 'src/logger';
import { ApplicationFlatTableEntity } from '../context/AppContextModels';
import { useAppContext } from '../context/AppContextProvider';
import { UserActionsEventTypes } from '../das-finsuite/das-finsuite-constants';

import './HomePageSideNavigationV2.scss';
import { useNavigationItems } from './useNavigationItems';
import { ExpandableGroup, NavLink } from './SideNavV2Components';
import { UserMetricsWidgetContent } from '../das-finsuite/widgets/user-metrics-widget/UserMetricsWidget';

/**
 * HomePageSideNavigationV2 - Custom side navigation that replicates
 * DFPHomePageSideNavigation without using Cloudscape SideNavigation.
 *
 * Drop-in replacement: swap <DFPHomePageSideNavigation /> with <HomePageSideNavigationV2 />
 */
export const HomePageSideNavigationV2: React.FC = () => {
  const navItems = useNavigationItems();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [activeHref, setActiveHref] = useState('');
  const finOpsContext = useFinTechContext();
  const { favoriteApplications, parsedApplications } = useAppContext();
  const [hasData, setHasData] = useState<StatusType>('loading');
  const [userMetricsData, setUserMetricsData] = useState<UserMetricsHomePageFinSuite[]>([]);

  const favoriteAppsList = useMemo<ApplicationFlatTableEntity[]>(() => {
    const activeParsedApps = parsedApplications.filter((app) => app.isActive);
    return activeParsedApps.filter((app) => favoriteApplications.includes(app.id));
  }, [favoriteApplications, parsedApplications]);

  useEffect(() => {
    setActiveHref(pathname + hash);
  }, [pathname, hash]);

  useEffect(() => {
    if (finOpsContext.userMetricsWithUserData.data && finOpsContext.userMetricsWithUserData.data.length > 0) {
      setUserMetricsData(returnFinSuiteUniqueUsersData(finOpsContext.userMetricsWithUserData.data));
      setHasData('finished');
    }
  }, [finOpsContext.userMetricsWithUserData.data]);

  const handleNavigate = (href: string, text: string, external?: boolean) => {
    if (external) return;
    logger.info('DaS FinSuite -> User Action: FinSuite Home page side nav', {
      event_type: UserActionsEventTypes.HOME_PAGE_SIDE_NAV_CLICK,
      value: text
    });
    // Open absolute URLs (e.g. Kingpin links) in a new tab
    if (href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(href, {
      state: { from: '/', redirectFromHomePage: true, fromAppName: '', fromAppId: '' }
    });
  };

  return (
    <div className="side-nav-v2-container">
      <div className="side-nav-v2-content">
        {navItems.map((item, index) => {
          if (item.type === 'divider') {
            return <div key={`divider-${index}`} className="side-nav-v2-divider" />;
          }
          if (item.type === 'section-header') {
            return (
              <div key={`section-header-${index}`} className="side-nav-v2-section-header">
                {item.href ? (
                  <Link
                    href={item.href}
                    onFollow={(e) => {
                      e.preventDefault();
                      handleNavigate(item.href, item.text);
                    }}
                  >
                    {item.text}
                  </Link>
                ) : (
                  item.text
                )}
              </div>
            );
          }
          if (item.type === 'expandable-group') {
            return <ExpandableGroup key={item.href} item={item} activeHref={activeHref} onNavigate={handleNavigate} />;
          }
          return <NavLink key={item.href} item={item} activeHref={activeHref} onNavigate={handleNavigate} />;
        })}

        {/* Favorites Section */}
        {favoriteAppsList.length > 0 && (
          <>
            <div className="side-nav-v2-divider" />
            <div className="side-nav-v2-favorites">
              <div className="side-nav-v2-favorites-header">
                <Box variant="h4">My Favorites</Box>
              </div>
              {favoriteAppsList.map((app) => (
                <div key={app.id} className="side-nav-v2-link">
                  <Link
                    href={app.embeddingEnabled ? `/all-apps/${app.id}` : app.applicationLink || '#'}
                    onFollow={(e) => {
                      e.preventDefault();
                      if (app.embeddingEnabled) {
                        handleNavigate(`/all-apps/${app.id}`, app.applicationName);
                      } else if (app.applicationLink) {
                        window.open(app.applicationLink, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    {app.applicationName}
                  </Link>
                  {/* {app.embeddingEnabled && (
                    <Box padding={{ left: 's' }}>
                      <Popover dismissButton={false} position="right" size="small" triggerType="custom" content="This is an Embedded app">
                        <Badge>E</Badge>
                      </Popover>
                    </Box>
                  )} */}
                </div>
              ))}
            </div>
          </>
        )}
        <div className="side-nav-v2-divider" />
      </div>
    </div>
  );
};
